<template>
    <div>
        <div class="mt-6 sm:mt-12 dashboard-global-settings main-bg-light">
            <div class="title">
                {{ $t('dashboard.global.settings.title')}}
            </div>
            <div class="divide-line"></div>
            <div class="flex items-center mb-8 mt-7">
                <div class="w-60">
                    <div class="flex items-center mb-3">
                        <div class="flex">
                            <IconDashboardName class="text-primary mb-1"/>
                        </div>
                        <div class="flex px-1 label-input main-text-color">
                            {{ $t('dashboard.dashboardName') }}
                        </div>
                    </div>
                    <base-input v-model="value.DashboardTitle" />
                </div>
                <div class="mx-5 w-60">
                    <LayoutManagementSelect v-on="$listeners"/>
                </div>
                <div v-if="currentDashboard && Object.keys(currentDashboard).length && currentDashboard.DashboardConf">
                    <GmtOffset
                        edit
                        :value="currentDashboard.DashboardConf.GMTOffsetData"
                        :error="error"
                        @update:value="(val) => currentDashboard.DashboardConf.GMTOffsetData = val"
                        @update:error="(val) => error = val"
                    />
                </div>
            </div>
            <div class="">
                <div class="shadow-base w-full px-5 py-4 rounded-lg"
                     v-if="activeLayout">
                    <div class="flex justify-between">
                        <div class="flex flex-col">
                            <div class="flex items-center mb-2">
                                <IconDashboardColor class="text-primary"/>
                                <span class="mx-1 main-text-color">{{ $t('dashboard.colors') }}</span>
                            </div>
                            <div class="flex flex-row">
                                <div v-for="group in getColorParameters"
                                     :key="group.JPath">
                                    <IconColorPicker class="w-6 h-6 mx-0-5"
                                                     :style="getIndicatorStyles(group)"/>
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col">
                            <div class="flex items-center mb-2">
                                <IconFonts class="text-primary"/>
                                <span class="mx-1 main-text-color">{{ $t('dashboard.fonts') }}</span>
                            </div>
                            <div class="text-xs font-normal main-text-color">
                                {{ getFontInfos }}
                            </div>
                        </div>
                        <div class="hidden flex-col lg:flex">
                            <div class="flex items-center mb-2">
                                <i>
                                    <IconTimer class="text-primary"/>
                                </i>
                                <span class="mx-1 main-text-color">{{ $t('dashboard.timeSettings') }}</span>
                            </div>
                            <div class="text-xs font-normal main-text-color">
                                {{ getTimeSettings }} <br>
                                {{ getReportSettings }}
                            </div>
                        </div>
                        <div
                            class="cursor-pointer main-text-color hover:text-primary"
                            v-if="!defaultLayout"
                            @click="onEditLayout"
                        >
                            <div class="flex items-center">
                                <IconPencil class="w-4 h-4" />
                                <div>{{ $t('common.edit') }}</div>
                            </div>
                        </div>
                        <el-tooltip
                            :content="$t('dashboard.defaultConfigConfirmation')"
                            placement="top"
                            v-if="defaultLayout"
                        >
                            <AlertTriangleIcon class="text-orange-500 cursor-help w-4-5 h-4-5"/>
                        </el-tooltip>
                    </div>
                    <div class="flex flex-col lg:hidden mt-4">
                        <div class="flex items-center mb-2">
                            <i>
                                <IconTimer class="text-primary"/>
                            </i>
                            <span class="mx-1 main-text-color">{{ $t('dashboard.timeSettings') }}</span>
                        </div>
                        <div class="text-xs main-text-color">
                            {{ getTimeSettings }} <br>
                            {{ getReportSettings }}
                        </div>
                    </div>
                    <div class="flex items-center flex-wrap" v-if="getSounds.length">
                        <template v-for="(sound, index) in getSounds">
                            <div v-if="sound && sounds[sound.LayoutParameterName]" :key="index" class="py-2 flex justify-between items-center w-1/2">
                                <div class="main-text-color" v-if="sound && sound.label">
                                    <span class="text-main-sm whitespace-nowrap main-text-color">
                                        {{ sound.label }}:
                                    </span>
                                </div>
                                <SoundSelector
                                    v-if="sound && sounds[sound.LayoutParameterName]"
                                    :sound-id="sound.LayoutParameterValueID"
                                    :src="sounds[sound.LayoutParameterName]"
                                    class="mx-2"
                                />
                            </div>
                        </template>
                    </div>
                </div>
            </div>
            <div class="flex justify-end mt-5">
                <save-button
                    icon="IconSave"
                    @on-click="saveDashboardChanges"
                />
            </div>
        </div>
       <div class="dashboard-global-settings mt-10 main-bg-light">
            <div class="title">
                {{ $t('dashboard.header.settings.title')}}
            </div>
            <div class="divide-line"></div>
            <div class="flex justify-between items-end">
                <div class="flex">
                    <div class="pt-4 pb-3">
                        <div class="flex items-center mb-4">
                            <div class="flex">
                                <IconDashboardColor class="text-primary"/>
                            </div>
                            <div class="flex mx-1 label-input main-text-color">{{ $t('dashboard.dashboardColor') }}</div>
                        </div>
                        <div class="flex flex-row items-center -mt-3">
                            <ColorParameterType v-bind="primaryColorSettings"
                                                v-if="!defaultLayout"
                                                :display-label="false"
                                                v-model="primaryColorSettings.Value">
                                <IconColorPickerSimple class="w-6 h-6 text-primary"/>
                            </ColorParameterType>
                            <div v-else
                                class="mt-4 flex items-center">
                                <IconColorPicker class="w-6 h-6 text-primary"/>
                                <el-tooltip :content="$t('dashboard.defaultConfigConfirmation')"
                                            placement="top">
                                    <AlertTriangleIcon class="text-orange-500 cursor-help w-4-5 h-4-5 mx-2"/>
                                </el-tooltip>
                            </div>
                        </div>
                    </div>
                    <div class="pt-4 pb-3 mx-8">
                        <div class="flex items-center mb-4">
                            <div class="flex">
                                <IconDashboardLogo class="text-primary mb-0-5"/>
                            </div>
                            <div class="flex mx-1 label-input main-text-color">{{ $t('dashboard.dashboardLogo') }}</div>
                        </div>
                        <div class="flex flex-row items-center">
                            <img :src="getLogo" alt="Logo"
                                class="h-6 object-cover">
                            <LayoutLogo class="mx-2"
                                        :disabled="defaultLayout"/>
                            <el-tooltip :content="$t('dashboard.defaultConfigConfirmation')"
                                        v-if="defaultLayout"
                                        placement="top">
                                <AlertTriangleIcon class="text-orange-500 cursor-help w-4-5 h-4-5"/>
                            </el-tooltip>
                        </div>
                    </div>
                    <div class="pt-4 pb-3">
                        <div class="flex items-center mb-4">
                            <div class="flex">
                                <IconDashboardLogo class="text-primary mb-0-5"/>
                            </div>
                            <div class="flex mx-1 label-input main-text-color">{{ $t('dashboard.favicon') }}</div>
                        </div>
                        <div class="flex flex-row items-center">
                            <img :src="getFavicon" alt="Favicon"
                                 class="h-6 object-cover">
                            <Favicon class="mx-2"
                                        :disabled="defaultLayout"/>
                            <el-tooltip :content="$t('dashboard.defaultConfigConfirmation')"
                                        v-if="defaultLayout"
                                        placement="top">
                                <AlertTriangleIcon class="text-orange-500 cursor-help w-4-5 h-4-5"/>
                            </el-tooltip>
                        </div>
                    </div>
                </div>
                <div>
                    <save-button
                        v-if="!defaultLayout"
                        icon="IconSave"
                        @on-click="saveLayoutChanges"
                    />
                </div>
            </div>
        </div>
        <WidgetGroupManagement
            v-if="currentDashboard.WidgetGroupList"
            class="w-full mt-12"
            v-on="$listeners"
            :widget-group-list="currentDashboard.WidgetGroupList"
        />
    </div>
</template>
<script>
    import { PenToolIcon } from 'vue-feather-icons'
    import { AlertTriangleIcon } from 'vue-feather-icons'
    import { DEFAULT_GROUP_KEYS, PRIMARY_COLOR_KEY } from '@/views/DashboardSettings/LayoutManagement/layout-management'
    import notification from '@/mixins/simpleNotification'
    import { DashboardApi } from '@/api/dashboardApi'
    import { LayoutApi } from '@/api/layoutApi'
    import cloneDeep from 'lodash/cloneDeep'
    import GmtOffset from '@/components/GmtOffsetBlock/GmtOffset.vue'
    import SoundSelector from '@/views/DashboardSettings/LayoutManagement/components/SoundSelector.vue'

    export default {
        components: {
            LayoutLogo: () => import('@/views/common/LayoutLogo'),
            Favicon: () => import('@/views/common/Favicon'),
            PenToolIcon,
            AlertTriangleIcon,
            ColorParameterType: () => import('@/views/DashboardSettings/LayoutManagement/components/ColorParameterType'),
            WidgetGroupManagement: () => import('@/views/DashboardSettings/components/WidgetGroupManagement'),
            LayoutManagementSelect: () => import('@/views/common/LayoutManagementSelect'),
            BaseInput: () => import('@/components/Common/BaseInput'),
            SaveButton: () => import("@/components/Common/Buttons/SaveButton"),
            GmtOffset,
            SoundSelector
        },
        props: {
            value: {
                type: Object,
                default: () => ({}),
            },
            currentDashboard: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                groupKeys: DEFAULT_GROUP_KEYS,
                error: '',
                sounds: {
                    SoundActiveCallWarning: null,
                    SoundActiveCallLimit: null,
                    SoundHoldTimeWarning: null,
                    SoundHoldTimeLimit: null
                }
            }
        },
        computed: {
            defaultLayout() {
                return this.activeLayout.LayoutAccountID === 0
            },
            getLogo() {
                return this.$store.getters['layout/getLogo']('activeLayout')
            },
            getFavicon() {
                return this.$store.getters['layout/getFavicon']('activeLayout')
            },
            activeLayout() {
                return this.$store.getters['layout/getActiveLayout']
            },
            getColorParameters() {
                const group = this.groupKeys['Colors']
                return this.getGroupedParameters(group)
            },
            getFontInfos() {
                const base = this.activeLayout.LayoutParametersList.find((el) => el.LayoutParameterName === 'FontSize')
                const header = this.activeLayout.LayoutParametersList.find((el) => el.LayoutParameterName === 'WidgetGroupTitlesFontSize')
                return `${this.$t('dashboard.base')} - ${base.Value}px, ${this.$t('dashboard.headers')} - ${header.Value}px`
            },
            getTimeSettings() {
                const MinRefreshInterval = this.activeLayout.LayoutParametersList.find((el) => el.LayoutParameterName === 'MinRefreshInterval')
                const ReportInterval = this.activeLayout.LayoutParametersList.find((el) => el.LayoutParameterName === 'ReportInterval')
                return `${this.$t('dashboard.refreshInterval')}: ${MinRefreshInterval.Value}s,   ${this.$t('dashboard.switchCategoryEvery')}: ${ReportInterval.Value}s`
            },
            getReportSettings() {
                const ReportSwitching = this.activeLayout.LayoutParametersList.find((el) => el.LayoutParameterName === 'ReportSwitching')
                const ReportRefresh = this.activeLayout.LayoutParametersList.find((el) => el.LayoutParameterName === 'ReportRefresh')
                const _ReportSwitching = ReportSwitching ? 'enabled' : 'disabled'
                const _ReportRefresh = ReportRefresh ? 'enabled' : 'disabled'
                return `${this.$t('dashboard.categorySwitching')} ${_ReportSwitching}, ${this.$t('dashboard.categoryRefresh')} ${_ReportRefresh}`
            },
            primaryColorSettings() {
                return this.activeLayout.LayoutParametersList.find((el) => el.LayoutParameterName === PRIMARY_COLOR_KEY)
            },
            warningSounds () {
                return this.$store.getters['entities/getWarningSounds']
            },
            errorSounds () {
                return this.$store.getters['entities/getErrorSounds']
            },
            getSounds() {
                const warningCallColors = this.activeLayout.LayoutParametersList.filter((el) => el.LayoutParameterName.includes('WarningColor'))
                const limitTimeColors = this.activeLayout.LayoutParametersList.filter((el) => el.LayoutParameterName.includes('LimitColor'))
                const sounds = this.activeLayout.LayoutParametersList.filter((el) => el.LayoutParameterName.includes('Sound'))
                if (sounds.length === 0) {
                    return []
                }

                return sounds.map(el => {
                    const makeFirstLetterInLowerCase = el.LayoutParameterName.charAt(0).toLowerCase() + el.LayoutParameterName.slice(1)
                    el.label = this.$t(`layout.config.${makeFirstLetterInLowerCase}`)
                    if (el.Value === '') {
                        el.Value = 'none'
                    } else {
                        const soundArray = el.LayoutParameterName.toLowerCase().includes('warning') ? this.warningSounds : this.errorSounds
                        const currentSound = soundArray.find(sound => sound.value === el.Value)
                        if (!currentSound) {
                            return
                        }
                        this.sounds[el.LayoutParameterName] = currentSound.sound
                    }

                    const key = `${el.LayoutParameterName.replace('Sound', '')}Color`
                    const allTimerColors = warningCallColors.concat(limitTimeColors)
                    const currentTimerColor = allTimerColors.find(el => el.LayoutParameterName === key)
                    el.color = {
                        color: (currentTimerColor && currentTimerColor.Value) || 'black'
                    } 

                    return el
                })
            },
        },
        methods: {
            getGroupedParameters(group) {
                return this.activeLayout.LayoutParametersList.filter((el) => group.includes(el.LayoutParameterName))
            },
            getIndicatorStyles(config) {
                return {
                    'color': config.Value,
                }
            },
            onEditLayout() {
                this.$router.push('layout-settings')
            },
            async saveDashboardChanges () {
                if (this.error) {
                    this.$notify({
                        type: 'danger',
                        icon: true,
                        title: this.$t('dashboard.emptyGMTValue', { value: this.error})
                    })
                    return
                }
                let data = cloneDeep(this.value)

                try {
                    this.loading = true
                    data.DashboardLayoutID = this.activeLayout.LayoutID
                    data.DashboardConf.GMTOffsetData = this.currentDashboard.DashboardConf.GMTOffsetData

                    delete data.WidgetGroupList

                    await DashboardApi.update(data)

                    const { DashboardID } = this.value
                    const dashboard = await DashboardApi.find(DashboardID)
                    await this.updateState(dashboard)
                } catch (e) {
                    console.warn(e)
                } finally {
                    notification.call({
                        type: 'success',
                        message: this.$t('common.changesSaved')
                    })

                }
            },
            async saveLayoutChanges () {
                try {
                    await LayoutApi.update(this.activeLayout)
                    await this.$store.dispatch('layout/setFavicon')
                } catch (e) {
                    console.warn(e)
                }
            },
            async updateState(dashboard) {
                this.$store.commit('layout/SET_ACTIVE_LAYOUT', this.activeLayout)
                await this.$store.dispatch('dashboards/updateCurrentDashboard', dashboard)
            }
        }
    }
</script>
<style lang="scss" scoped>
.label-input {
    @apply flex text-main-sm font-medium truncate;
}
.dashboard-global-settings {
    @apply border rounded-xl border-gray-500 p-5;
}
.divide-line {
    @apply border-b-4 my-3 border-primary;
}
.title {
    @apply text-primary text-2xl;
}
::v-deep .input-element-wrapper .form-control {
    background: var(--mainBgColor-color);
    border: 1px solid var(--mainBgColor-color);
    color: var(--mainTextColor-color);
}
</style>
